import { FC, useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import type { Battery } from '@/hooks/useBatteryContainers';

interface EditBatteryModalProps {
  open: boolean;
  onClose: () => void;
  battery: Battery | null;
  onUpdate: (id: string, updates: Partial<Battery>) => Promise<void>;
}

export const EditBatteryModal: FC<EditBatteryModalProps> = ({
  open,
  onClose,
  battery,
  onUpdate,
}) => {
  const [serialNumber, setSerialNumber] = useState('');
  const [status, setStatus] = useState<'vrij' | 'gereserveerd' | 'ingepland' | 'geplaatst' | 'te_bestellen'>('vrij');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (battery) {
      setSerialNumber(battery.serial_number || '');
      setStatus(battery.status);
      setNotes(battery.notes || '');
    }
  }, [battery]);

  const handleUpdate = async () => {
    if (!battery) return;

    setLoading(true);
    try {
      await onUpdate(battery.id, {
        serial_number: serialNumber || null,
        status,
        notes: notes || null,
      });
      toast.success('Batterij bijgewerkt');
      handleClose();
    } catch (error) {
      console.error('Error updating battery:', error);
      toast.error('Fout bij bijwerken batterij');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSerialNumber('');
    setStatus('vrij');
    setNotes('');
    setLoading(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Batterij Bewerken</DialogTitle>
        </DialogHeader>

        {battery && (
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              <p><strong>Type:</strong> {battery.battery_type?.name}</p>
              <p><strong>Capaciteit:</strong> {battery.capacity_kwh} kWh</p>
              <p><strong>Vermogen:</strong> {battery.power_kw} kW</p>
            </div>

            <div>
              <Label>Serienummer</Label>
              <Input
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                placeholder="SN-12345"
              />
            </div>

            <div>
              <Label>Status</Label>
              <Select value={status} onValueChange={(val: any) => setStatus(val)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vrij">Vrij</SelectItem>
                  <SelectItem value="gereserveerd">Gereserveerd</SelectItem>
                  <SelectItem value="ingepland">Ingepland</SelectItem>
                  <SelectItem value="geplaatst">Geplaatst</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Notities</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Extra informatie..."
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Annuleren
          </Button>
          <Button onClick={handleUpdate} disabled={loading}>
            {loading ? 'Bijwerken...' : 'Bijwerken'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
